/**
 * Background Sync Queue
 * Queues failed POST/PUT/DELETE requests when offline
 * and replays them when connectivity is restored.
 */

const DB_NAME = 'dcca-sync';
const STORE_NAME = 'requests';
const SYNC_TAG = 'dcca-sync-queue';

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** Queue a failed request for background sync */
export async function queueRequest(url, method, headers, body) {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).add({
      url,
      method,
      headers: Object.fromEntries(
        Object.entries(headers).filter(([k]) => k.toLowerCase() !== 'content-length')
      ),
      body: body ? (typeof body === 'string' ? body : JSON.stringify(body)) : null,
      timestamp: Date.now(),
    });
    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = reject;
    });

    // Request background sync if supported
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      const reg = await navigator.serviceWorker.ready;
      await reg.sync.register(SYNC_TAG);
    }

    return true;
  } catch (err) {
    console.error('Failed to queue request:', err);
    return false;
  }
}

/** Get count of pending queued requests */
export async function getPendingCount() {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    return new Promise((resolve) => {
      const countReq = store.count();
      countReq.onsuccess = () => resolve(countReq.result);
      countReq.onerror = () => resolve(0);
    });
  } catch {
    return 0;
  }
}

/** Get all pending requests */
export async function getPendingRequests() {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    return new Promise((resolve) => {
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve([]);
    });
  } catch {
    return [];
  }
}

/** Manually flush the queue (when back online) */
export async function flushQueue() {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const items = await new Promise((resolve) => {
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve([]);
    });

    let synced = 0;
    for (const item of items) {
      try {
        const response = await fetch(item.url, {
          method: item.method,
          headers: item.headers,
          body: item.body,
        });
        if (response.ok || response.status < 500) {
          // Remove from queue
          const delTx = db.transaction(STORE_NAME, 'readwrite');
          delTx.objectStore(STORE_NAME).delete(item.id);
          await new Promise((r) => { delTx.oncomplete = r; });
          synced++;
        }
      } catch {
        // Still offline, stop trying
        break;
      }
    }
    return synced;
  } catch {
    return 0;
  }
}

/** Clear the entire queue */
export async function clearQueue() {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).clear();
    await new Promise((r) => { tx.oncomplete = r; });
  } catch {
    // ignore
  }
}
